import { Descriptions, Modal, Tag, Typography } from 'antd';

import { ApiLog } from '@/types/apis/admin';
import { formatDateTime } from '@/utils/dateFormatter';

const { Text } = Typography;

interface ApiLogDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiLog: ApiLog | null;
}

export default function ApiLogDetailModal({
  isOpen,
  onClose,
  apiLog,
}: ApiLogDetailModalProps) {
  if (!apiLog) return null;

  const formatTimestamp = (timestamp: number) => {
    return formatDateTime(new Date(timestamp)).replace(/\//g, '-');
  };

  const safeParseJSON = (value: string | undefined): unknown => {
    if (!value) return null;
    try {
      return typeof value === 'string' ? JSON.parse(value) : value;
    } catch {
      return value;
    }
  };

  const formatMetadataValue = (value: string | undefined) => {
    if (!value) return '-';

    const parsed = safeParseJSON(value);
    if (parsed === null) return '-';

    if (typeof parsed === 'object') {
      return <pre>{JSON.stringify(parsed, null, 2)}</pre>;
    }

    return <pre>{String(parsed)}</pre>;
  };

  const basicItems = [
    {
      key: 'id',
      label: '日誌 ID',
      children: (
        <Text code copyable>
          {apiLog.id}
        </Text>
      ),
    },
    {
      key: 'requestId',
      label: '請求 ID',
      children: apiLog.requestId ? (
        <Text code copyable>
          {apiLog.requestId}
        </Text>
      ) : (
        '-'
      ),
    },
    {
      key: 'type',
      label: '類型',
      children: <Tag>{apiLog.type}</Tag>,
    },
    {
      key: 'timestamp',
      label: '時間',
      children: formatTimestamp(apiLog.createdAt),
    },
  ];

  const detailItems = [
    {
      key: 'errorName',
      label: '名稱',
      children: apiLog.errorName || '-',
    },
    {
      key: 'message',
      label: '訊息',
      children: <pre>{apiLog.message}</pre>,
      span: 3,
    },
  ];

  if (apiLog.stack) {
    detailItems.push({
      key: 'stack',
      label: '堆疊追蹤',
      children: <pre>{apiLog.stack}</pre>,
      span: 3,
    });
  }

  const userItems = [
    {
      key: 'userId',
      label: '用戶 ID',
      children: apiLog.userId ?? '-',
    },
  ];

  const requestItems = [
    {
      key: 'method',
      label: '請求方法',
      children: apiLog.requestMethod ? (
        <Tag color="blue">{apiLog.requestMethod}</Tag>
      ) : (
        '-'
      ),
    },
    {
      key: 'path',
      label: '請求路徑',
      children: apiLog.requestPath || '-',
    },
    {
      key: 'statusCode',
      label: '狀態碼',
      children: apiLog.statusCode ? (
        <Tag color={apiLog.statusCode < 400 ? 'green' : 'red'}>
          {apiLog.statusCode}
        </Tag>
      ) : (
        '-'
      ),
    },
    {
      key: 'duration',
      label: '請求時間',
      children: apiLog.requestDuration ? `${apiLog.requestDuration}ms` : '-',
    },
  ];

  const metadataItems =
    apiLog.metadata && Object.keys(apiLog.metadata).length > 0
      ? Object.entries(apiLog.metadata).map(([key, value]) => ({
          key,
          label: key,
          children: formatMetadataValue(value),
          span: 3,
        }))
      : [];

  return (
    <Modal
      title="API 日誌詳細資訊"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <div>
        <Descriptions
          title="基本資訊"
          bordered
          items={basicItems}
          size="small"
        />

        <Descriptions
          title="詳細資訊"
          bordered
          items={detailItems}
          size="small"
        />

        <Descriptions
          title="用戶資訊"
          bordered
          items={userItems}
          size="small"
        />

        <Descriptions
          title="請求資訊"
          bordered
          items={requestItems}
          size="small"
        />

        {metadataItems.length > 0 && (
          <Descriptions
            title="額外資訊"
            bordered
            items={metadataItems}
            size="small"
          />
        )}
      </div>
    </Modal>
  );
}
